/**
 * Platform key status context provider.
 * Checks whether the platform LLM API key is configured and provides
 * that status to all components. Only fetches for support+ users.
 */

import type { ReactNode } from 'react'
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { useUserRoleContext } from './UserRoleContext'
import { hasSupportAccess } from '../utils/roleUtils'
import { platformTokenService } from '../services/platformToken.service'

interface PlatformKeyStatusContextType {
    isPlatformKeyMissing: boolean
    loading: boolean
    error: Error | null
    /** Re-fetch the platform key status from the API. Call after save/delete operations. */
    refreshStatus: () => Promise<void>
}

const PlatformKeyStatusContext = createContext<PlatformKeyStatusContextType | undefined>(undefined)

/**
 * Hook to access platform key status from context.
 * Must be used within a PlatformKeyStatusProvider.
 */
// eslint-disable-next-line react-refresh/only-export-components -- Justified: custom hook export
export const usePlatformKeyStatus = (): PlatformKeyStatusContextType => {
    const context = useContext(PlatformKeyStatusContext)
    if (context === undefined) {
        throw new Error('usePlatformKeyStatus must be used within a PlatformKeyStatusProvider')
    }
    return context
}

interface PlatformKeyStatusProviderProps {
    children: ReactNode
    /** Skip API fetch (for Storybook/tests). Uses initialKeyMissing directly. */
    skipFetch?: boolean
    /** Initial platform key missing state when skipFetch is true. Default: false. */
    initialKeyMissing?: boolean
}

export const PlatformKeyStatusProvider: React.FC<PlatformKeyStatusProviderProps> = ({
    children,
    skipFetch = false,
    initialKeyMissing = false,
}) => {
    const { role, loading: roleLoading } = useUserRoleContext()

    const [isPlatformKeyMissing, setIsPlatformKeyMissing] = useState(
        skipFetch ? initialKeyMissing : false
    )
    const [loading, setLoading] = useState(!skipFetch)
    const [error, setError] = useState<Error | null>(null)

    /**
     * Fetch platform token status from the API.
     * Shared by the initial useEffect and the public refreshStatus callback.
     */
    const fetchStatus = useCallback(async (signal?: { cancelled: boolean }) => {
        try {
            setLoading(true)
            setError(null)
            const status = await platformTokenService.getTokenStatusReadOnly()
            if (signal?.cancelled) return
            setIsPlatformKeyMissing(!status.exists)
        } catch (err) {
            if (signal?.cancelled) return
            // On error, hide banner (don't show misleading info)
            setIsPlatformKeyMissing(false)
            setError(err instanceof Error ? err : new Error('Unknown error'))
        } finally {
            if (!signal?.cancelled) {
                setLoading(false)
            }
        }
    }, [])

    useEffect(() => {
        if (skipFetch) return

        // Still waiting for role to resolve
        if (roleLoading) {
            setIsPlatformKeyMissing(false)
            setLoading(true)
            return
        }

        // User doesn't have support+ access — don't fetch
        if (!hasSupportAccess(role)) {
            setIsPlatformKeyMissing(false)
            setLoading(false)
            return
        }

        const signal = { cancelled: false }
        void fetchStatus(signal)
        return () => {
            signal.cancelled = true
        }
    }, [skipFetch, role, roleLoading, fetchStatus])

    /**
     * Public callback for consumers to trigger a re-fetch
     * (e.g. after saving or deleting the platform token).
     * No-op when skipFetch is true (Storybook/tests).
     */
    const refreshStatus = useCallback(async () => {
        if (skipFetch) return
        await fetchStatus()
    }, [skipFetch, fetchStatus])

    const value = useMemo<PlatformKeyStatusContextType>(
        () => ({
            isPlatformKeyMissing,
            loading,
            error,
            refreshStatus,
        }),
        [isPlatformKeyMissing, loading, error, refreshStatus]
    )

    return (
        <PlatformKeyStatusContext.Provider value={value}>
            {children}
        </PlatformKeyStatusContext.Provider>
    )
}
